import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Paperclip, Send, FileText } from "lucide-react";
import { MAROON, inpL } from "./LeaveForm";

export const SPK_STATUS_LABEL = { menunggu: "Menunggu", selesai_sebagian: "Selesai Sebagian", menunggu_verifikasi: "Menunggu Verifikasi", selesai: "Selesai", perlu_perbaikan: "Perlu Perbaikan" };
export const SPK_STATUS_CHIP = { menunggu: "bg-slate-100 text-slate-500", selesai_sebagian: "bg-amber-50 text-amber-600", menunggu_verifikasi: "bg-sky-50 text-sky-600", selesai: "bg-emerald-50 text-emerald-600", perlu_perbaikan: "bg-red-50 text-red-600" };
export const PRIORITY_LABEL = { normal: "Normal", penting: "Penting", mendesak: "Mendesak" };
export const PRIORITY_CHIP = { normal: "bg-slate-100 text-slate-500", penting: "bg-amber-50 text-amber-600", mendesak: "bg-red-50 text-red-600" };
export const FB_TYPES = [{ id: "kendala", label: "Kendala" }, { id: "pertanyaan", label: "Pertanyaan" }, { id: "hasil", label: "Hasil" }];
export const FB_CHIP = { kendala: "bg-amber-50 text-amber-600", pertanyaan: "bg-sky-50 text-sky-600", hasil: "bg-emerald-50 text-emerald-600" };

export function fmtWaktu(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const tanggal = d.toLocaleDateString("id-ID", { timeZone: "Asia/Jakarta", day: "numeric", month: "short", year: "numeric" });
    const jam = d.toLocaleTimeString("id-ID", { timeZone: "Asia/Jakarta", hour: "2-digit", minute: "2-digit" }).replace(":", ".");
    return `${tanggal}, ${jam}`;
  } catch { return iso; }
}

export function fmtDeadline(dl) {
  if (!dl) return "—";
  const [d, t] = dl.split("T");
  try {
    const tanggal = new Date(`${d}T00:00:00`).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });
    return `${tanggal}, ${(t || "").replace(":", ".")}`;
  } catch { return dl; }
}

export default function SpkFeedback({ spkId, feedback = [], onChanged, tid = "fb" }) {
  const [filter, setFilter] = useState("semua");
  const [type, setType] = useState("kendala");
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const shown = feedback.filter((f) => !f.reply_to && (filter === "semua" || f.type === filter));
  const repliesOf = (id) => feedback.filter((f) => f.reply_to === id);

  const submit = async () => {
    if (text.trim().length < 2) return toast.error("Isi feedback wajib diisi");
    setBusy(true);
    try {
      let file_url = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        file_url = (await api.post("/uploads", fd)).data.url;
      }
      await api.post(`/spk/${spkId}/feedback`, { type, text: text.trim(), file_url });
      toast.success("Feedback terkirim");
      setText(""); setFile(null);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengirim feedback"); } finally { setBusy(false); }
  };

  const Item = ({ f, nested }) => (
    <div data-testid={`${tid}-item-${f.id}`} className={`flex gap-2.5 ${nested ? "ml-8 mt-2" : ""}`}>
      <span className="w-8 h-8 rounded-full bg-[#FBF3F4] text-[#7F1D2D] flex items-center justify-center text-xs font-bold flex-shrink-0">{(f.user_name || "?")[0]}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-bold text-slate-800">{f.user_name}</span>
          <span className="text-[9px] text-slate-400">{fmtWaktu(f.created_at)}</span>
          <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${FB_CHIP[f.type]}`}>{FB_TYPES.find((t) => t.id === f.type)?.label}</span>
        </div>
        <div className="text-xs text-slate-600 bg-slate-50 rounded-lg rounded-tl-none px-3 py-2 mt-1 inline-block max-w-full">{f.text}</div>
        {f.file_url && <a href={f.file_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[10px] text-sky-600 font-semibold mt-1 hover:underline"><FileText className="w-3 h-3" /> Lampiran</a>}
      </div>
    </div>
  );

  return (
    <div data-testid={`${tid}-panel`} className="space-y-3">
      <div className="flex gap-1.5 flex-wrap">
        {[{ id: "semua", label: "Semua" }, ...FB_TYPES].map((t) => (
          <button key={t.id} data-testid={`${tid}-tab-${t.id}`} onClick={() => setFilter(t.id)}
            className={`h-8 px-3 rounded-full text-[11px] font-semibold border transition-colors ${filter === t.id ? "border-[#7F1D2D] bg-[#7F1D2D] text-white" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>{t.label}</button>
        ))}
      </div>
      <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
        {shown.length === 0 && <div className="text-center py-6 text-[11px] text-slate-400">Belum ada feedback.</div>}
        {shown.map((f) => (
          <div key={f.id}>
            <Item f={f} />
            {repliesOf(f.id).map((r) => <Item key={r.id} f={r} nested />)}
          </div>
        ))}
      </div>
      <div className="border-t border-slate-100 pt-3 space-y-2">
        <select data-testid={`${tid}-type`} value={type} onChange={(e) => setType(e.target.value)} className={inpL}>
          {FB_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        <textarea data-testid={`${tid}-text`} value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Tulis feedback atau pertanyaan..." className="w-full px-3 py-2.5 rounded-xl bg-white border border-slate-200 text-xs text-slate-700 focus:outline-none focus:border-[#7F1D2D]" />
        <div className="flex items-center gap-2">
          <label data-testid={`${tid}-file`} className="h-10 px-3 rounded-xl border border-dashed border-slate-300 text-[11px] text-slate-500 cursor-pointer hover:border-[#7F1D2D] hover:text-[#7F1D2D] transition-colors flex items-center gap-1.5 flex-1 min-w-0">
            <Paperclip className="w-3.5 h-3.5 flex-shrink-0" /> <span className="truncate">{file ? file.name : "Lampirkan file"}</span>
            <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
          <button data-testid={`${tid}-send`} onClick={submit} disabled={busy} className="h-10 px-4 rounded-xl text-white text-xs font-semibold flex items-center gap-1.5 disabled:opacity-40" style={{ background: MAROON }}>
            <Send className="w-3.5 h-3.5" /> {busy ? "Mengirim..." : "Kirim Feedback"}
          </button>
        </div>
        <p className="text-[9px] text-slate-400">Feedback tidak otomatis menandai tugas selesai.</p>
      </div>
    </div>
  );
}
